/**
 * @recommend итоговая структура:
 * - FilterListTemplate/useFilterListTemplate.ts: хук useFilterListTemplate
 * - FilterListTemplate/FilterListTemplate.tsx: презентационный компонент (только JSX + useFilterListTemplate)
 */
import s from './FilterListTemplate.module.sass'

import FilterButtons from '../FilterButtons/FilterButtons'

import { FC, useEffect, useState } from 'react'

import { useAppDispatch, useAppSelector } from '@app/model/store/client'

import { TypeFiltersByKeyList, TypeTag } from '@features/Restaurant/RestaurantFilters/model/types'

import {
  getFilterByType,
  getFilterSelected,
  DEFAULT_SEARCH_FIELD,
  getListBySearchValue,
  applySearchToLists,
  toCheckboxOption,
} from '@features/Restaurant/RestaurantFilters/helpers'

import {
  closeModalFilterList,
  resetFilterAsync,
  setCheckedFilterItemAsync,
} from '@features/Restaurant/RestaurantFilters/model/thunks'

import { BlockBoundary } from '@shared/lib/components/BlockBoundary'
import { BtnFilterDropdown } from '@shared/ui/Form/Single/Filters/Dropdown'
import { Checkbox } from '@shared/ui/Form/Single/Checkbox'
import { CheckboxOption } from '@shared/types/fields/fields'
import { EGG } from '@shared/api/analytics'
import { Search } from '@shared/ui/Form/Build/Search'
import { Text } from '@shared/ui/Typography/Text'
import { useClientResponsive } from '@shared/lib/hooks/useClientResponsive'

interface Props {
  typeFilter: TypeFiltersByKeyList
  handleSave: () => void
  handleRemove: () => void
}

const title: Partial<Record<TypeFiltersByKeyList, string>> = {
  establishment: 'Тип заведения',
  metro: 'Метро',
  kitchen: 'Кухня',
}

type FilterListChangeChecked = (option: CheckboxOption, checked: boolean) => void

type ListWithChangeChecked = {
  list: TypeTag[]
  changeChecked: FilterListChangeChecked
}

type RenderFilterListItemParams = {
  tag: TypeTag
  changeChecked: FilterListChangeChecked
  isMobile: boolean
}

type BuildListContentParams = ListWithChangeChecked & { isMobile: boolean }
type BuildPrecheckedBlockParams = ListWithChangeChecked

type UseFilterListTemplateParams = {
  typeFilter: TypeFiltersByKeyList
  handleSave: () => void
  handleRemove: () => void
}

/**
 * @function renderFilterListItem
 * рендерит элемент списка (Text на мобильном, Checkbox на десктопе)
 * @recommend оставить в этом файле или вынести в FilterListTemplate/renderFilterListItem.tsx
 */
const renderFilterListItem = (params: RenderFilterListItemParams) => {
  const { tag, changeChecked, isMobile } = params
  const option = toCheckboxOption(tag)
  const mobileView = isMobile && (
    <Text
      key={`default-${tag.id}`}
      sizes={'M'}
      className={s['tag-mobile']}
      onClick={() => changeChecked(option, !tag.checked)}>
      {tag.name}
    </Text>
  )
  const desktopView = !isMobile && (
    <Checkbox key={`default-${tag.id}`} option={option} checked={tag.checked} onChange={changeChecked} />
  )
  return [mobileView, desktopView].find(Boolean) || null
}

/**
 * @function buildListContent
 * собирает контент списка: либо элементы, либо «Ничего не найдено»
 * @recommend оставить в этом файле или вынести в FilterListTemplate/buildListContent.tsx
 */
const buildListContent = (params: BuildListContentParams) => {
  const { list, changeChecked, isMobile } = params
  return [
    list.length > 0 && list.map((tag) => renderFilterListItem({ tag, changeChecked, isMobile })),
    !list.length && <Text sizes={'M'}>Ничего не найдено</Text>,
  ].find(Boolean)
}

/**
 * @function buildPrecheckedBlock
 * собирает блок пред-выбранных чекбоксов с тенями
 * @recommend оставить в этом файле или вынести в FilterListTemplate/buildPrecheckedBlock.tsx
 */
const buildPrecheckedBlock = (params: BuildPrecheckedBlockParams) => {
  const { list, changeChecked } = params

  const checkboxes = list.map((tag) => (
    <Checkbox key={`checked-${tag.id}`} option={toCheckboxOption(tag)} checked={tag.checked} onChange={changeChecked} />
  ))

  if (!list.length) return null

  return (
    <>
      <div className={s['list-checked-wrapper']}>
        <div className={`${s['list-checked-shadow']} ${s['list-checked-shadow--top']}`}></div>
        <div className={s['list-checked']}>{checkboxes}</div>
        <div className={`${s['list-checked-shadow']} ${s['list-checked-shadow--bottom']}`}></div>
      </div>
      <div className={s.divider}></div>
    </>
  )
}

/**
 * @function useFilterListTemplate
 * @description хук логики списка фильтров: поиск, prechecked, handlers
 * @recommend вынести в FilterListTemplate/useFilterListTemplate.ts
 */
const useFilterListTemplate = (params: UseFilterListTemplateParams) => {
  const { typeFilter, handleSave, handleRemove } = params

  const dispatch = useAppDispatch()
  const stateFilters = useAppSelector((store) => store.features.restaurant_filters)
  const { list, preCheckedList, checkedList, visible } = stateFilters.filters[typeFilter]
  const [tagSearchList, setTagSearchList] = useState<TypeTag[]>([])
  const [tagPreCheckedList, setTagPreCheckedList] = useState<TypeTag[]>([])
  const [isOpenedPopup, setIsOpenedPopup] = useState(false)
  const { isMobile } = useClientResponsive()
  const [searchField, setSearchField] = useState(DEFAULT_SEARCH_FIELD)

  const resetSearchField = () => setSearchField((prev) => ({ ...prev, value: '' }))
  const closePopupAndReset = () => {
    setIsOpenedPopup(false)
    resetSearchField()
  }

  const hasSearchValue = !!searchField.value
  const useList = getListBySearchValue(hasSearchValue, tagSearchList, list)
  const usePrecheckedList = getListBySearchValue(hasSearchValue, tagPreCheckedList, preCheckedList)

  useEffect(() => {
    applySearchToLists({
      value: searchField.value,
      list,
      preCheckedList,
      setTagSearchList,
      setTagPreCheckedList,
    })
  }, [stateFilters.filters[typeFilter]])

  const handleSearchTag = (value: string) => {
    applySearchToLists({ value, list, preCheckedList, setTagSearchList, setTagPreCheckedList })
  }

  const changeChecked = (option: CheckboxOption, checked: boolean) => {
    dispatch(setCheckedFilterItemAsync({ filterType: typeFilter, itemId: Number(option.id), checked }))
  }

  const handleOpenPopup = () => setIsOpenedPopup(true)
  const handleClosePopup = () => {
    dispatch(closeModalFilterList(typeFilter))
    closePopupAndReset()
  }
  const handleRemoveInner = async () => {
    closePopupAndReset()
    await dispatch(resetFilterAsync(typeFilter))
    handleRemove()
  }
  const handleSaveInner = () => {
    EGG.common.filter_use_click({
      filter_apply_type: typeFilter,
      filters_apply: getFilterSelected(),
      filter_apply_value: getFilterByType({ key: typeFilter }),
    })
    closePopupAndReset()
    handleSave()
  }

  const listContent = buildListContent({ list: useList, changeChecked, isMobile })
  const precheckedBlock = buildPrecheckedBlock({ list: usePrecheckedList, changeChecked })

  const handlers = {
    open: handleOpenPopup,
    close: handleClosePopup,
    search: handleSearchTag,
    remove: handleRemoveInner,
    save: handleSaveInner,
  }

  return { visible, isOpenedPopup, searchField, setSearchField, listContent, precheckedBlock, checkedList, handlers }
}

const FilterListTemplate: FC<Props> = ({ typeFilter, handleSave, handleRemove }) => {
  const { visible, isOpenedPopup, searchField, setSearchField, listContent, precheckedBlock, checkedList, handlers } =
    useFilterListTemplate({ typeFilter, handleSave, handleRemove })

  if (!visible) return null

  return (
    <BlockBoundary>
      <div>
        <BtnFilterDropdown openedPopup={isOpenedPopup}>
          <BtnFilterDropdown.Button
            handleOpen={handlers.open}
            handleClose={handlers.close}
            count={checkedList?.length || 0}
            handleRemove={handlers.remove}>
            {title[typeFilter]}
          </BtnFilterDropdown.Button>

          <BtnFilterDropdown.Popup
            title={title[typeFilter]}
            width={'420px'}
            className={s.popup}
            handleClose={handlers.close}>
            <Search field={searchField} setField={setSearchField} handleChange={handlers.search} />

            <div className={s['wrapper-lists']}>
              {precheckedBlock}

              <div className={s['list-wrapper']}>
                <div className={`${s['list-shadow']} ${s['list-shadow--top']}`}></div>
                <div className={s.list}>{listContent}</div>
                <div className={`${s['list-shadow']} ${s['list-shadow--bottom']}`}></div>
              </div>
            </div>
            <FilterButtons handleRemove={handlers.remove} handleSave={handlers.save} />
          </BtnFilterDropdown.Popup>
        </BtnFilterDropdown>
      </div>
    </BlockBoundary>
  )
}

export default FilterListTemplate
